type OrgTrendPoint = {
  period: string;
  avg_composite: number | null;
};

const W = 460;
const H = 200;
const M = 36;
const dataPadding = 30; // Padding from Y-axis to first data point

type Props = {
  points: OrgTrendPoint[];
};

const OrgCompositeTrendChart = ({ points }: Props) => {
  const publishedPoints = points.filter(
    (p): p is OrgTrendPoint & { avg_composite: number } =>
      p.avg_composite != null
  );

  if (publishedPoints.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-500">
        No published trend data yet.
      </div>
    );
  }

  const series = publishedPoints.map(p => p.avg_composite);
  const quarters = publishedPoints.map(p => p.period);

  const min = 1;
  const max = 5;
  const range = max - min;

  const pts = series
    .map((v, i) => {
      if (v == null) return null;
      const x =
        M +
        dataPadding +
        (i / Math.max(series.length - 1, 1)) * (W - M - dataPadding - 15);
      const y = H - M - ((v - min) / range) * (H - M - 10);
      return { x, y, v, q: quarters[i] };
    })
    .filter(
      (p): p is { x: number; y: number; v: number; q: string } => p != null
    );

  const path = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(" ");

  const areaPath =
    path +
    ` L${pts[pts.length - 1].x.toFixed(1)},${H - M}` +
    ` L${pts[0].x.toFixed(1)},${H - M} Z`;

  return (
    <svg
      className="block w-full h-auto max-w-[480px] mx-auto"
      viewBox={`0 0 ${W} ${H}`}
    >
      <defs>
        <linearGradient id="orgTrendGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#9333EA" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#9333EA" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[1, 2, 3, 4, 5].map(t => {
        const y = H - M - ((t - min) / range) * (H - M - 10);
        return (
          <g key={t}>
            <line
              x1={M + dataPadding}
              y1={y}
              x2={W - 15}
              y2={y}
              stroke="#f3f4f6"
            />
            <text
              x={M - 6}
              y={y + 3}
              fontSize={9}
              fill="#9ca3af"
              textAnchor="end"
            >
              {t}
            </text>
          </g>
        );
      })}
      <path d={areaPath} fill="url(#orgTrendGrad)" />
      <path d={path} fill="none" stroke="#9333EA" strokeWidth={2.2} />
      {pts.map(p => (
        <circle
          key={p.q}
          cx={p.x}
          cy={p.y}
          r={4}
          fill="#9333EA"
          stroke="#fff"
          strokeWidth={2}
        >
          <title>
            {p.q}: {p.v.toFixed(2)}
          </title>
        </circle>
      ))}
      {quarters.map((q, i) => {
        const x =
          M +
          dataPadding +
          (i / Math.max(quarters.length - 1, 1)) * (W - M - dataPadding - 15);
        return (
          <text
            key={q}
            x={x}
            y={H - 10}
            fontSize={9}
            fill="#6b7280"
            textAnchor="middle"
          >
            {q}
          </text>
        );
      })}
    </svg>
  );
};

export default OrgCompositeTrendChart;
